import { useState } from 'react';
import { Variable } from 'lucide-react';

/**
 * The inspector's shared form primitives.
 *
 * Extracted from NodeInspector.jsx when the node registry outgrew one file: the flow-control
 * editors (FlowNodeConfigs.jsx) and the schema-driven tool argument form
 * (ToolArgumentsForm.jsx) need the same label/input/variable-picker vocabulary, and copying
 * three Tailwind class strings into three files is how a panel starts looking like three
 * different panels.
 *
 * Deliberately not twreact-ui components: these are the dense, 10px-label controls the
 * inspector column already uses, not the app's standard form controls. The repo rule
 * ("missing UI component → the twreact-ui repo") is about reusable app-level components;
 * this is one panel's internal styling, shared between that panel's own files.
 */

export const textareaClass = 'w-full border rounded-md px-2 py-1 text-sm bg-[var(--app-surface)] font-mono';
export const inputClass = 'w-full border rounded-md px-2 py-1 text-sm bg-[var(--app-surface)]';

export function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-secondary-500">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-secondary-500">{hint}</span>}
    </label>
  );
}

/**
 * Upstream node ids as ready-made template references. The expression/template language
 * addresses earlier nodes by id, and node ids are generated (`llm_1`), so without this the
 * user has to read them off the canvas and retype them exactly.
 *
 * `onInsert` receives the reference text; every caller appends it to the field it belongs
 * to, because there is no cursor position to insert at through a controlled textarea.
 */
export function VariableMenu({ nodeIds, onInsert, label = 'Insert variable' }) {
  const [open, setOpen] = useState(false);
  if (!nodeIds.length) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] text-primary-600"
      >
        <Variable size={11} /> {label}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-56 max-h-48 overflow-y-auto rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] shadow-lg">
          {nodeIds.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => { onInsert(`{{${id}.output}}`); setOpen(false); }}
              className="w-full text-left px-2 py-1 text-[11px] font-mono hover:bg-[var(--app-border)]/30"
            >
              {`{{${id}.output}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The workflow picker shared by the Sub-workflow and For Each nodes. Keyed by `key`, not
 *  id: a graph is exported/imported and copied between installs, and a key survives that
 *  where an auto-increment id does not. `excludeKey` keeps a workflow out of its own list —
 *  the engine refuses direct self-calls anyway, so offering it would only build a graph
 *  that fails at run time. */
export function WorkflowSelect({ value, onChange, workflows, excludeKey, hint }) {
  const options = (workflows || []).filter((w) => w.key !== excludeKey);
  return (
    <Field label="Workflow" hint={hint}>
      <select className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>Select a workflow…</option>
        {options.map((w) => <option key={w.key} value={w.key}>{w.name} ({w.key})</option>)}
      </select>
      {value && !options.some((w) => w.key === value) && (
        <span className="block text-[10px] text-amber-600">
          {`"${value}" is not one of this company's workflows — the run will fail.`}
        </span>
      )}
    </Field>
  );
}
